/**
 * Authentication Navigator for Portfolio Tracker.
 *
 * Controls the authentication flow:
 * - Shows Login/Register screens when user is not authenticated
 * - Shows TabNavigator when user is authenticated
 * NOW THEME-AWARE
 */

import { createStackNavigator } from '@react-navigation/stack';
import { ActivityIndicator, View, StyleSheet } from 'react-native';
import { useMemo } from 'react';

import { TabNavigator } from './TabNavigator';
import { DashboardNavigator } from './DashboardNavigator';
import { PortfolioNavigator } from './PortfolioNavigator';
import { NotificationsNavigator } from './NotificationsNavigator';
import { LoginScreen } from '../screens/auth/LoginScreen';
import { RegisterScreen } from '../screens/auth/RegisterScreen';
import { VerifyEmailScreen } from '../screens/auth/VerifyEmailScreen';
import { PasswordResetScreen } from '../screens/auth/PasswordResetScreen';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

export type AuthNavigatorParamList = {
  Main: undefined;
  // Retired tab navigators stay reachable from the root stack (see below).
  Dashboard: undefined;
  Portfolio: undefined;
  Notifications: undefined;
  Login: undefined;
  Register: undefined;
  VerifyEmail: { email?: string } | undefined;
  PasswordReset: undefined;
};

const Stack = createStackNavigator<AuthNavigatorParamList>();

export function AuthNavigator() {
  const { isAuthenticated, isInitialized } = useAuth();
  const { theme } = useTheme();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  if (!isInitialized) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  return (
    <Stack.Navigator screenOptions={{ headerShown: false }}>
      {isAuthenticated ? (
        <>
          <Stack.Screen name="Main" component={TabNavigator} />
          {/* Retired tab navigators (chat-centric revamp §6.7) stay registered
              because live screens still navigate into them — Feed → InboxReading,
              SoulBadge → Portfolio — until the T8/T9 sweep. Settings is not here
              any more: it is a fourth tab inside TabNavigator (2026-11-01 spec),
              so a root registration would just shadow itself. */}
          <Stack.Screen name="Dashboard" component={DashboardNavigator} />
          <Stack.Screen name="Portfolio" component={PortfolioNavigator} />
          <Stack.Screen name="Notifications" component={NotificationsNavigator} />
        </>
      ) : (
        <>
          <Stack.Screen name="Login" component={LoginScreen} />
          <Stack.Screen name="Register" component={RegisterScreen} />
          <Stack.Screen name="VerifyEmail" component={VerifyEmailScreen} />
          <Stack.Screen name="PasswordReset" component={PasswordResetScreen} />
        </>
      )}
    </Stack.Navigator>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
  });
}
